import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Card, Popover } from 'antd'
import dayjs from 'dayjs'
import {
  dateFormatYearMonthDay,
  downLoadFileBlob,
  formatEndTime,
  formatMessageNew,
  formatStartTime,
  initValueAndLabel,
  numFormat,
  uniqueFun
} from '@/utils/common'
import { getReworkSummaryData, getReworkSummaryDataDownload } from '@/services/rfid/reports/rfidReworkSummary'
import SearchBar from '@/components/searchbar/SearchBar'
import AntdTable from '@/components/table/AntdTable'
import { BUSINESS_TYPE_REWORK, CARTON_PE } from '@/utils/types'
import ActionBar from '../../../components/table/ActionBar'
import { InfoCircleOutlined } from '@ant-design/icons'
import { useSelector } from 'react-redux'
import { modalError } from '../../../utils/modalUtils'
import { getOutboundScanDropList } from '../../../services/rfid/reports/rfidTunnelScanAccuracyReport'

const defaultBusinessType = '1'
const startMonth = dayjs().subtract(1, 'day')
const endMonth = dayjs().subtract(1, 'day')
const RfidReworkSummary = () => {
  const deviceSize = useSelector(state => state.commonStore.deviceSize)
  const [filtersState] = useState({})
  const [tableList, setTableList] = useState([])
  const [queryFlag, setQueryFlag] = useState(false)
  const [businessType, setBusinessType] = useState(defaultBusinessType)
  const [grDate, setGrDate] = useState([startMonth, endMonth])
  const [reworkDate, setReworkDate] = useState([startMonth, startMonth])
  const mounting = useRef(false)
  const language = useSelector(state => state.commonStore.language)
  const getGrDateLabel = useCallback(() => {
    if (businessType === '1') {
      return language === 'zh_CN' ? 'GR 日期' : 'GR Date'
    } else if (businessType === '2') {
      return language === 'zh_CN' ? 'GI 日期' : 'GI Date'
    }
    return formatMessageNew('languageObj.rfidReworkSummary.grDate')
  }, [businessType, language])

  let formRef = useRef(null)

  // searchListInit 只用于初始化，label 用占位符，后续用 useEffect 动态更新
  const searchListInit = [
    {
      type: 'select',
      label: 'languageObj.rfidReworkSummary.businessType',
      key: 'businessType',
      initialValues: defaultBusinessType,
      selectList: BUSINESS_TYPE_REWORK,
      handleChange: (e, idx, item) => {
        setBusinessType(e)
      },
      spans: {
        xl: 8,
        xxl: 8
      }
    },
    {
      type: 'timeRange',
      label: '', // 占位，后续动态设置
      key: 'grDate',
      format: dateFormatYearMonthDay,
      initialValues: [startMonth, endMonth],
      handleChange: (e, idx, item) => {
        setGrDate(e)
      },
      spans: {
        xl: 8,
        xxl: 8
      },
      allowClear: true
    },
    {
      type: 'timeRange',
      label: 'languageObj.rfidReworkSummary.reworkDate',
      key: 'reworkDate',
      format: dateFormatYearMonthDay,
      initialValues: [startMonth, endMonth],
      handleChange: (e, idx, item) => {
        setReworkDate(e)
      },
      spans: {
        xl: 8,
        xxl: 8
      },
      allowClear: true
    },
    {
      type: 'select',
      label: 'languageObj.rfidReworkSummary.plant',
      key: 'plant',
      allowClear: true,
      initialValues: [],
      mode: 'multiple',
      maxTagCount: 1,
      selectList: [],
      spans: {
        xl: 8,
        xxl: 8
      }
    },
    {
      type: 'select',
      label: 'languageObj.rfidReworkDetail.cartonPe',
      key: 'cartonPe',
      allowClear: true,
      initialValues: [],
      mode: 'multiple',
      maxTagCount: 1,
      selectList: CARTON_PE,
      spans: {
        xl: 8,
        xxl: 8
      }
    }
  ]

  const [searchList, setSearchList] = useState(() => {
    // 初始化时设置 grDate 的 label
    const list = [...searchListInit]
    list[1].label = getGrDateLabel()
    return list
  })

  // 监听 businessType，动态更新 grDate 的 label
  useEffect(() => {
    setSearchList(prev => {
      const newList = [...prev]
      newList.forEach(item => {
        if (item.key === 'grDate') {
          item.label = getGrDateLabel()
        }
      })
      return newList
    })
  }, [businessType, getGrDateLabel, language])

  const tableColumns = [
    {
      title: () => formatMessageNew('languageObj.rfidConfiguration.siteType'),
      dataIndex: 'siteType',
      width: 80,
      fixed: 'left'
    },
    {
      title: () => formatMessageNew('languageObj.rfidConfiguration.siteName'),
      dataIndex: 'siteName',
      width: 80,
      fixed: 'left'
    },
    {
      title: () => formatMessageNew('languageObj.rfidReworkSummary.plant'),
      dataIndex: 'plant',
      width: 80,
      fixed: 'left'
    },
    {
      title: () => formatMessageNew('languageObj.rfidReworkSummary.byCarton'),
      dataIndex: 'carton',
      children: [
        {
          title: () => formatMessageNew('languageObj.rfidReworkSummary.giedASNQty'),
          dataIndex: 'giAsnQty',
          width: 170,
          render: text => {
            return <div>{numFormat(text)}</div>
          }
        },
        {
          title: () => formatMessageNew('languageObj.rfidReworkSummary.reworkQty'),
          dataIndex: 'reworkQty',
          width: 150,
          render: text => {
            return <div>{numFormat(text)}</div>
          }
        },
        {
          title: () => formatMessageNew('languageObj.rfidReworkSummary.rework'),
          dataIndex: 'reworkRate',
          width: 80,
          render: text => {
            return <div>{numFormat(text)}</div>
          }
        },
        {
          title: () => formatMessageNew('languageObj.rfidReworkSummary.specialProductQty'),
          dataIndex: 'specialProductQty',
          width: 150,
          render: text => {
            return <div>{numFormat(text)}</div>
          }
        },
        {
          title: () => formatMessageNew('languageObj.rfidReworkSummary.productShortageQty'),
          dataIndex: 'productShortageQty',
          width: 150,
          render: text => {
            return <div>{numFormat(text)}</div>
          }
        },
        {
          title: () => formatMessageNew('languageObj.rfidReworkSummary.productOverageQty'),
          dataIndex: 'productOverageQty',
          width: 150,
          render: text => {
            return <div>{numFormat(text)}</div>
          }
        },
        {
          title: () => formatMessageNew('languageObj.rfidReworkSummary.physicalSkuIncorrectQty'),
          dataIndex: 'productErrorQty',
          width: 160,
          render: text => {
            return <div>{numFormat(text)}</div>
          }
        },
        {
          title: () => formatMessageNew('languageObj.rfidReworkSummary.manualUnitScanQty'),
          dataIndex: 'manualProcessingQty',
          width: 150,
          render: text => {
            return <div>{numFormat(text)}</div>
          }
        },
        {
          title: () => formatMessageNew('languageObj.rfidReworkSummary.submitSysUpdateQty'),
          dataIndex: 'noProcessingSubmissionQty',
          width: 175,
          render: text => {
            return <div>{numFormat(text)}</div>
          }
        },
        {
          title: () => formatMessageNew('languageObj.rfidReworkSummary.relabelQty'),
          dataIndex: 'reLabelingQty',
          width: 150,
          render: text => {
            return <div>{numFormat(text)}</div>
          }
        },
        {
          title: () => formatMessageNew('languageObj.rfidReworkSummary.relabel'),
          dataIndex: 'reLabelingRate',
          width: 150,
          render: text => {
            return <div>{numFormat(text)}</div>
          }
        },
        {
          title: () => formatMessageNew('languageObj.rfidReworkSummary.noRfidTagQty'),
          dataIndex: 'rfidMissingQty',
          width: 150,
          render: text => {
            return <div>{numFormat(text)}</div>
          }
        },
        {
          title: () => formatMessageNew('languageObj.rfidReworkSummary.scanNoResponseQty'),
          dataIndex: 'rfidNoResponseQty',
          width: 150,
          render: text => {
            return <div>{numFormat(text)}</div>
          }
        },
        {
          title: () => formatMessageNew('languageObj.rfidReworkSummary.multipleRfidCartonQty'),
          dataIndex: 'multiLabelQty',
          width: 175,
          render: text => {
            return <div>{numFormat(text)}</div>
          }
        },
        {
          title: () => formatMessageNew('languageObj.rfidReworkSummary.physicalSkuCorrectQty'),
          dataIndex: 'wrongLabelQty',
          width: 150,
          render: text => {
            return <div>{numFormat(text)}</div>
          }
        }
      ]
    },
    {
      title: () => formatMessageNew('languageObj.rfidReworkSummary.byUnit'),
      dataIndex: 'unit',
      children: [
        {
          title: () => formatMessageNew('languageObj.rfidReworkSummary.giedASNQtyByUnit'),
          dataIndex: 'giAsnUnitQty',
          width: 170,
          render: text => {
            return <div>{numFormat(text)}</div>
          }
        },
        {
          title: () => formatMessageNew('languageObj.rfidReworkSummary.reworkQtyByUnit'),
          dataIndex: 'reworkUnitQty',
          width: 150,
          render: text => {
            return <div>{numFormat(text)}</div>
          }
        },
        {
          title: () => formatMessageNew('languageObj.rfidReworkSummary.rework'),
          dataIndex: 'reworkUnitRate',
          width: 80,
          render: text => {
            return <div>{numFormat(text)}</div>
          }
        },
        {
          title: () => formatMessageNew('languageObj.rfidReworkSummary.specialProductQtyByUnit'),
          dataIndex: 'specialProductUnitQty',
          width: 150,
          render: text => {
            return <div>{numFormat(text)}</div>
          }
        },
        {
          title: () => formatMessageNew('languageObj.rfidReworkSummary.physicalSkuIncorrectQtyByUnit'),
          dataIndex: 'productErrorUnitQty',
          width: 160,
          render: text => {
            return <div>{numFormat(text)}</div>
          }
        },
        {
          title: () => formatMessageNew('languageObj.rfidReworkSummary.relabelQtyByUnit'),
          dataIndex: 'reLabelingUnitQty',
          width: 150,
          render: text => {
            return <div>{numFormat(text)}</div>
          }
        },
        {
          title: () => formatMessageNew('languageObj.rfidReworkSummary.relabel'),
          dataIndex: 'reLabelingUnitRate',
          width: 150,
          render: text => {
            return <div>{numFormat(text)}</div>
          }
        },
        {
          title: () => formatMessageNew('languageObj.rfidReworkSummary.noRfidTagQtyByUnit'),
          dataIndex: 'rfidMissingUnitQty',
          width: 150,
          render: text => {
            return <div>{numFormat(text)}</div>
          }
        },
        {
          title: () => formatMessageNew('languageObj.rfidReworkSummary.scanNoResponseQtyByUnit'),
          dataIndex: 'rfidNoResponseUnitQty',
          width: 150,
          render: text => {
            return <div>{numFormat(text)}</div>
          }
        },
        {
          title: () => formatMessageNew('languageObj.rfidReworkSummary.multipleRfidCartonQtyByUnit'),
          dataIndex: 'multiLabelUnitQty',
          width: 175,
          render: text => {
            return <div>{numFormat(text)}</div>
          }
        },
        {
          title: () => formatMessageNew('languageObj.rfidReworkSummary.physicalSkuCorrectQtyByUnit'),
          dataIndex: 'wrongLabelUnitQty',
          width: 150,
          render: text => {
            return <div>{numFormat(text)}</div>
          }
        }
      ]
    }
  ]
  useEffect(() => {
    mounting.current && searchDataFun()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryFlag])
  useEffect(() => {
    findPlantModulesFun()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const findPlantModulesFun = () => {
    getOutboundScanDropList().then(res => {
      searchListInit.forEach(itemFirst => {
        if (itemFirst.key === 'plant') {
          const arr = (res.resultData.plants || ['1025', '1027']).filter(item => item === '1025' || item === '1027')
          itemFirst.selectList = initValueAndLabel(uniqueFun(arr, 3))
        }
      })
      setSearchList([...searchListInit])
    })
  }

  const downLoadFun = () => {
    const params = {
      ...getParams()
    }
    getReworkSummaryData(params)
      .then(res => {
        if (res) {
          getReworkSummaryDataDownload(params).then(res => {
            downLoadFileBlob(res)
          })
        }
      })
      .catch(err => {
        modalError({
          content: err.errorMessage
        })
      })
  }

  const searchDataBtn = () => {
    mounting.current = true
    setQueryFlag(!queryFlag)
  }
  const searchClearDataBtn = () => {
    mounting.current = true
    formRef && formRef.current.clearForm()
    setQueryFlag(!queryFlag)
  }

  const getParams = () => {
    const info = (formRef.current && formRef.current.getSearchValue()) || {}
    const { grDate, reworkDate } = info
    const params = {
      ...info,
      grDateFrom: formatStartTime(grDate),
      grDateTo: formatEndTime(grDate),
      reworkDateFrom: formatStartTime(reworkDate),
      reworkDateTo: formatEndTime(reworkDate),
      isChinese: language === 'zh_CN' ? true : false
    }

    // 如果 plant 未选择，则使用 searchList 中 plant 的所有选项值作为查询
    const plantItem = (searchList || []).find(item => item.key === 'plant')
    const allPlants = plantItem && Array.isArray(plantItem.selectList) ? plantItem.selectList.map(v => v.value) : []

    params.plant = info.plant && Array.isArray(info.plant) && info.plant.length > 0 ? info.plant : allPlants

    delete params.grDate
    delete params.reworkDate
    return params
  }

  const searchDataFun = () => {
    const params = {
      ...getParams()
    }
    // 只保留summary查询
    getReworkSummaryData(params)
      .then(res => {
        if (res && res.resultData) {
          let dt = res.resultData
          setTableList(dt)
        }
      })
      .catch(err => {
        modalError({
          content: err.errorMessage
        })
      })
  }

  const getPopoverContent = () => {
    return (
      <div style={{ maxWidth: 800 }}>
        <div style={{ fontWeight: 700, marginBottom: 8 }}>Data Dictionary</div>
        <div style={{ fontWeight: 500, margin: '8px 0 4px' }}>*该报表只统计用户选择时间范围内箱最后一次rework结果</div>
        <div style={{ fontWeight: 700, margin: '12px 0 4px' }}>By Carton</div>
        <table className="popover-table">
          <tbody>
            <tr>
              <td>GIed/ASN Qty</td>
              <td>T-HUB从SAP中消费到的箱数量</td>
            </tr>
            <tr>
              <td>Rework Qty</td>
              <td>经过开箱检查的箱数（不包含查看系统最新扫描结果及环扫操作）</td>
            </tr>
            <tr>
              <td>Rework %</td>
              <td>Rework Qty / (ASN/GI Qty)</td>
            </tr>
            <tr>
              <td>Special Product Qty</td>
              <td>用户在开箱扫描首页点击“特殊产品”按钮并成功提交的箱数</td>
            </tr>
            <tr>
              <td>Product Shortage Qty</td>
              <td>用户在开箱扫描首页点击“实物少件”按钮并成功提交的箱数</td>
            </tr>
            <tr>
              <td>Product Overage Qty</td>
              <td>用户在开箱扫描首页点击“实物多件”按钮并成功提交的箱数</td>
            </tr>
            <tr>
              <td>Physical SKU Incorrect Qty</td>
              <td>用户在开箱逐件扫描过程中点击“实物SKU错误”并提交的箱数</td>
            </tr>
            <tr>
              <td>Manual Unit Scan Qty</td>
              <td>用户在开箱扫描首页点击“转入人工逐件扫描”并至少扫描了一件产品并成功提交的箱数</td>
            </tr>
            <tr>
              <td>Submit W/O SYS Update Qty</td>
              <td>用户逐件扫描过程中无进行补标/多标/错标处理且成功提交的箱数</td>
            </tr>
            <tr>
              <td>Relabel Qty</td>
              <td>用户逐件扫描过程中做过补标处理的箱数</td>
            </tr>
            <tr>
              <td>Relabel %</td>
              <td>Relabel Qty / (ASN/GI Qty)</td>
            </tr>
            <tr>
              <td>No RFID Tag Qty</td>
              <td>用户逐件扫描过程中记录过由于“产品无RFID标签”而进行补标处理的箱数</td>
            </tr>
            <tr>
              <td>Scan No Response Qty</td>
              <td>用户逐件扫描过程中记录过由于“RFID扫描无反应”而进行补标处理的箱数</td>
            </tr>
            <tr>
              <td>Multiple RFID Carton Qty</td>
              <td>用户逐件扫描过程中做过多标处理（一件实物产品上有多个RFID标签）的箱数</td>
            </tr>
            <tr>
              <td>Physical SKU Correct Qty</td>
              <td>用户逐件扫描过程中做过错标处理（确认实物SKU正确）的箱数</td>
            </tr>
          </tbody>
        </table>
        <div style={{ fontWeight: 700, margin: '12px 0 4px' }}>By Unit</div>
        <table className="popover-table">
          <tbody>
            <tr>
              <td>ASN/GI Qty</td>
              <td>T-HUB从SAP中消费到的箱内产品件数</td>
            </tr>
            <tr>
              <td>Rework Qty</td>
              <td>经过开箱检查的箱内产品件数（不包含查看系统最新扫描结果及环扫操作）</td>
            </tr>
            <tr>
              <td>Rework %</td>
              <td>Rework Qty / (ASN/GI Qty)</td>
            </tr>
            <tr>
              <td>Special Product Qty</td>
              <td>用户在开箱扫描首页点击“特殊产品”按钮并成功提交的箱内件数</td>
            </tr>
            <tr>
              <td>Physical SKU Incorrect Qty</td>
              <td>用户在开箱逐件扫描过程中点击“实物SKU错误”并提交的箱内件数</td>
            </tr>
            <tr>
              <td>Relabel Qty</td>
              <td>用户逐件扫描过程中做过补标处理的件数</td>
            </tr>
            <tr>
              <td>Relabel %</td>
              <td>Relabel Qty / (ASN/GI Qty)</td>
            </tr>
            <tr>
              <td>No RFID Tag Qty</td>
              <td>用户逐件扫描过程中记录过由于“产品无RFID标签”而进行补标处理的件数</td>
            </tr>
            <tr>
              <td>Scan No Response Qty</td>
              <td>用户逐件扫描过程中记录过由于“RFID扫描无反应”而进行补标处理的件数</td>
            </tr>
            <tr>
              <td>Multiple RFID Carton Qty</td>
              <td>用户逐件扫描过程中做过多标处理（一件实物产品上有多个RFID标签）的件数</td>
            </tr>
            <tr>
              <td>Physical SKU Correct Qty</td>
              <td>用户逐件扫描过程中做过错标处理（确认实物SKU正确）的件数</td>
            </tr>
          </tbody>
        </table>
      </div>
    )
  }

  return (
    <div>
      <Card bordered={false} bodyStyle={{ padding: 4 }}>
        <div className="search-bar">
          <SearchBar
            ref={formRef}
            searchList={searchList}
            showSearchBtn={true}
            showClearBtn={true}
            onSearch={searchDataBtn}
            onClear={searchClearDataBtn}
          />
        </div>
        <div className="content-container">
          <ActionBar onDownload={downLoadFun} style={{ alignItems: 'end' }}>
            <div style={{ display: 'flex', alignItems: 'flex-end', marginLeft: 20 }}>
              <Popover
                trigger="click"
                color="#f5f5f5"
                className="cursorPointer"
                overlayStyle={{
                  maxWidth: '50vw',
                  maxHeight: '95vh',
                  overflow: 'auto',
                  borderRadius: 8
                }}
                placement="right"
                content={getPopoverContent()}
              >
                <InfoCircleOutlined style={{ fontSize: 18 * deviceSize, marginBottom: 10 * deviceSize }} />
              </Popover>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-end',
                  marginLeft: '20px'
                }}
              >
                <span
                  style={{
                    fontSize: 15,
                    fontWeight: 500
                  }}
                >
                  {(businessType === '1' ? 'GR ' : 'GI ') +
                    formatMessageNew('languageObj.rfidReworkSummary.grDate') +
                    ': ' +
                    dayjs(formatStartTime(grDate)).format('YYYY-MM-DD') +
                    ' to ' +
                    dayjs(formatEndTime(grDate)).format('YYYY-MM-DD')}
                </span>
                <span
                  style={{
                    fontSize: 15,
                    fontWeight: 500
                  }}
                >
                  {formatMessageNew('languageObj.rfidReworkSummary.reworkDate') +
                    ': ' +
                    dayjs(formatStartTime(reworkDate)).format('YYYY-MM-DD') +
                    ' to ' +
                    dayjs(formatEndTime(reworkDate)).format('YYYY-MM-DD')}
                </span>
              </div>
            </div>
          </ActionBar>
          <AntdTable
            // key={tableKey}
            dataSource={tableList}
            columns={tableColumns}
            // 独一无二的表示，不能重复
            columnsKey="rfidReworkSummaryReportColumns"
            // pagination={pagination}
            // onChange={handleTableChange}
            filters={filtersState} // 绑定 filtersState
          />
        </div>
      </Card>
    </div>
  )
}
export default RfidReworkSummary
